/**
 * Pipeline List View Component
 * Table-based view of opportunities with sorting and inline actions
 */
import React, { useState, useMemo, useCallback } from 'react';
import { 
  ArrowUpDown, ArrowUp, ArrowDown, MoreHorizontal, 
  Calendar, Package, Globe, Eye, ChevronRight,
  TrendingUp, AlertCircle
} from 'lucide-react';
import { formatCompactCurrency } from '../../api/pipeline';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

// Stage configuration
const STAGE_CONFIG = {
  target: { label: 'Target', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  qualified: { label: 'Qualified', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  rfq: { label: 'RFQ', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  quoted: { label: 'Quoted', color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  awarded: { label: 'Awarded', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  sourced: { label: 'Sourced', color: 'bg-green-100 text-green-700 border-green-200' },
  lost: { label: 'Lost', color: 'bg-slate-100 text-slate-700 border-slate-200' },
};

// Priority configuration
const PRIORITY_CONFIG = {
  high: { label: 'High', color: 'bg-red-100 text-red-700 border-red-200' },
  medium: { label: 'Medium', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  low: { label: 'Low', color: 'bg-slate-100 text-slate-600 border-slate-200' },
};

// Sortable header component - defined outside to avoid recreation
const SortableHeader = ({ label, sortKey, sortConfig, onSort, className = '' }) => {
  const getSortIcon = () => {
    if (sortConfig.key !== sortKey) {
      return <ArrowUpDown className="w-4 h-4 text-slate-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-blue-600" />
      : <ArrowDown className="w-4 h-4 text-blue-600" />;
  };

  return (
    <th 
      className={`px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors ${className}`}
      onClick={() => onSort(sortKey)}
    >
      <div className="flex items-center gap-1">
        {label}
        {getSortIcon()}
      </div>
    </th>
  );
};

const KanbanListView = ({
  opportunities,
  onCardClick,
  onStageChange,
  onArchive,
}) => {
  const [sortConfig, setSortConfig] = useState({ key: 'revenue', direction: 'desc' });

  // Handle sort
  const handleSort = useCallback((key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  }, []);

  // Flatten opportunities from all stages
  const allOpportunities = useMemo(() => {
    const flat = [];
    Object.entries(opportunities).forEach(([stage, opps]) => {
      opps.forEach(opp => {
        flat.push({ ...opp, stage });
      });
    });
    return flat;
  }, [opportunities]);

  // Sort opportunities
  const sortedOpportunities = useMemo(() => {
    const sorted = [...allOpportunities];
    sorted.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.key) {
        case 'program_name':
          aVal = a.program_name?.toLowerCase() || '';
          bVal = b.program_name?.toLowerCase() || '';
          break;
        case 'oem':
          aVal = a.oem?.toLowerCase() || '';
          bVal = b.oem?.toLowerCase() || '';
          break;
        case 'stage':
          const stageOrder = ['target', 'qualified', 'rfq', 'quoted', 'awarded', 'sourced', 'lost'];
          aVal = stageOrder.indexOf(a.stage);
          bVal = stageOrder.indexOf(b.stage);
          break;
        case 'priority':
          const priorityOrder = { high: 0, medium: 1, low: 2 };
          aVal = priorityOrder[a.priority] ?? 3;
          bVal = priorityOrder[b.priority] ?? 3;
          break;
        case 'revenue':
          aVal = a.revenue_potential_cents || 0;
          bVal = b.revenue_potential_cents || 0;
          break;
        case 'confidence':
          aVal = a.confidence_percent || 0;
          bVal = b.confidence_percent || 0;
          break;
        case 'weighted':
          aVal = (a.revenue_potential_cents || 0) * (a.confidence_percent || 0) / 100;
          bVal = (b.revenue_potential_cents || 0) * (b.confidence_percent || 0) / 100;
          break;
        case 'sop_date':
          aVal = a.sop_date ? new Date(a.sop_date).getTime() : 0;
          bVal = b.sop_date ? new Date(b.sop_date).getTime() : 0;
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [allOpportunities, sortConfig]);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      year: 'numeric' 
    });
  };

  // Get initials
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  if (allOpportunities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-slate-500">
        <AlertCircle className="w-12 h-12 mb-4 text-slate-300" />
        <p className="text-lg font-medium">No opportunities found</p>
        <p className="text-sm">Try adjusting your filters</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <SortableHeader label="Opportunity" sortKey="program_name" sortConfig={sortConfig} onSort={handleSort} className="min-w-[250px]" />
              <SortableHeader label="OEM" sortKey="oem" sortConfig={sortConfig} onSort={handleSort} className="min-w-[120px]" />
              <SortableHeader label="Stage" sortKey="stage" sortConfig={sortConfig} onSort={handleSort} className="min-w-[100px]" />
              <SortableHeader label="Priority" sortKey="priority" sortConfig={sortConfig} onSort={handleSort} className="min-w-[90px]" />
              <SortableHeader label="Revenue" sortKey="revenue" sortConfig={sortConfig} onSort={handleSort} className="min-w-[100px]" />
              <SortableHeader label="Confidence" sortKey="confidence" sortConfig={sortConfig} onSort={handleSort} className="min-w-[100px]" />
              <SortableHeader label="Weighted" sortKey="weighted" sortConfig={sortConfig} onSort={handleSort} className="min-w-[100px]" />
              <SortableHeader label="SOP" sortKey="sop_date" sortConfig={sortConfig} onSort={handleSort} className="min-w-[90px]" />
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider min-w-[80px]">
                Owner
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider w-16">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sortedOpportunities.map((opp) => {
              const stageConfig = STAGE_CONFIG[opp.stage] || STAGE_CONFIG.target;
              const priorityConfig = PRIORITY_CONFIG[opp.priority] || PRIORITY_CONFIG.low;
              const weightedValue = (opp.revenue_potential_cents || 0) * (opp.confidence_percent || 0) / 100;

              return (
                <tr 
                  key={opp.id}
                  className="hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => onCardClick(opp)}
                >
                  {/* Opportunity Name */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {opp.program_name}
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                          {opp.platform_code || opp.region || '-'}
                        </p>
                      </div>
                    </div>
                  </td>

                  {/* OEM */}
                  <td className="px-4 py-3">
                    <span className="text-sm text-slate-700">{opp.oem}</span>
                  </td>

                  {/* Stage */}
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={`text-xs ${stageConfig.color}`}>
                      {stageConfig.label}
                    </Badge>
                  </td>

                  {/* Priority */}
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={`text-xs ${priorityConfig.color}`}>
                      {priorityConfig.label}
                    </Badge>
                  </td>

                  {/* Revenue */}
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-slate-900">
                      {formatCompactCurrency(opp.revenue_potential_cents)}
                    </span>
                  </td>

                  {/* Confidence */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden max-w-[60px]">
                        <div
                          className="h-full rounded-full"
                          style={{ 
                            width: `${opp.confidence_percent || 0}%`,
                            backgroundColor: (opp.confidence_percent || 0) >= 75 ? '#22c55e' : 
                                           (opp.confidence_percent || 0) >= 50 ? '#0ea5e9' : '#f59e0b'
                          }}
                        />
                      </div>
                      <span className="text-xs text-slate-600 w-8">
                        {opp.confidence_percent || 0}%
                      </span>
                    </div>
                  </td>

                  {/* Weighted Value */}
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-emerald-600">
                      {formatCompactCurrency(weightedValue)}
                    </span>
                  </td>

                  {/* SOP Date */}
                  <td className="px-4 py-3">
                    <span className="text-sm text-slate-600">
                      {formatDate(opp.sop_date)}
                    </span>
                  </td>

                  {/* Owner */}
                  <td className="px-4 py-3">
                    {opp.assigned_to ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Avatar className="w-7 h-7">
                              <AvatarFallback className="text-xs bg-blue-500 text-white">
                                {getInitials(opp.assigned_to.name)}
                              </AvatarFallback>
                            </Avatar>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">{opp.assigned_to.name}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <span className="text-xs text-slate-400">Unassigned</span>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={() => onCardClick(opp)}>
                          <ChevronRight className="w-4 h-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => onStageChange?.(opp.id, 'qualified')}>
                          Move to Qualified
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onStageChange?.(opp.id, 'rfq')}>
                          Move to RFQ
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onStageChange?.(opp.id, 'quoted')}>
                          Move to Quoted
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onStageChange?.(opp.id, 'awarded')}>
                          Move to Awarded
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          className="text-red-600"
                          onClick={() => onArchive?.(opp.id)}
                        >
                          Archive
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer with count */}
      <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 text-sm text-slate-600">
        Showing {sortedOpportunities.length} opportunities
      </div>
    </div>
  );
};

export default KanbanListView;
